import React from 'react';

// Flowguard Logo - Modern shield with upward-flowing arrow
// Colors: Deep navy blue (#0A1628) and vibrant orange (#FF9500)
export const FlowguardLogo = ({ className = "", size = "md", showText = true, variant = "default" }) => {
  // Size configurations
  const sizes = {
    sm: { icon: 28, text: "text-lg", deals: "text-xs" },
    md: { icon: 36, text: "text-xl", deals: "text-sm" },
    lg: { icon: 44, text: "text-2xl", deals: "text-base" },
    xl: { icon: 52, text: "text-3xl", deals: "text-lg" }
  };
  
  const { icon: iconSize, text: textSize, deals: dealsSize } = sizes[size] || sizes.md;
  
  // Color variants
  const textColor = variant === "light" ? "#ffffff" : "#0A1628";

  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      {/* Shield Icon with Flowing Arrow */}
      <svg 
        width={iconSize} 
        height={iconSize} 
        viewBox="0 0 64 64" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Gradient for the arrow */}
        <defs>
          <linearGradient id={`arrowGrad-${size}`} x1="20" y1="50" x2="44" y2="14" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#FF7A00" />
            <stop offset="50%" stopColor="#FF9500" />
            <stop offset="100%" stopColor="#FFB347" />
          </linearGradient>
        </defs>
        
        {/* Shield outline - modern sleek design */}
        <path 
          d="M32 4L8 14V28C8 43.5 18.5 57.5 32 62C45.5 57.5 56 43.5 56 28V14L32 4Z" 
          fill="none"
          stroke="white"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        {/* Flowing arrow - dynamic upward curve */}
        <path 
          d="M22 44C22 44 26 36 32 30C38 24 42 22 42 22"
          stroke={`url(#arrowGrad-${size})`}
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        
        {/* Arrow head */}
        <path 
          d="M36 16L42 22L48 16"
          stroke={`url(#arrowGrad-${size})`}
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        
        {/* Secondary flow line for depth */}
        <path 
          d="M18 48C18 48 24 38 32 32C36 28 40 26 40 26"
          stroke="#FF9500"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity="0.4"
        />
      </svg>
      
      {/* Text */}
      {showText && (
        <div className="flex flex-col leading-tight">
          <span 
            className={`font-bold ${textSize} font-['Inter','Manrope',sans-serif] tracking-tight text-white`}
          >
            FLOWGUARD
          </span>
          <span 
            className={`${dealsSize} font-medium font-['Inter','Manrope',sans-serif]`}
            style={{ color: '#FF9500' }}
          >
            .deals
          </span>
        </div>
      )}
    </div>
  );
};

// Horizontal lockup version (text beside icon, single line)
export const FlowguardLogoHorizontal = ({ className = "", size = "md", variant = "default" }) => {
  const sizes = {
    sm: { icon: 24, text: "text-base", deals: "text-xs" },
    md: { icon: 32, text: "text-xl", deals: "text-sm" },
    lg: { icon: 40, text: "text-2xl", deals: "text-base" }
  };
  
  const { icon: iconSize, text: textSize, deals: dealsSize } = sizes[size] || sizes.md;
  const textColor = variant === "light" ? "#ffffff" : "#0A1628";

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <svg 
        width={iconSize} 
        height={iconSize} 
        viewBox="0 0 64 64" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={`arrowGradH-${size}`} x1="20" y1="50" x2="44" y2="14" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#FF7A00" />
            <stop offset="50%" stopColor="#FF9500" />
            <stop offset="100%" stopColor="#FFB347" />
          </linearGradient>
        </defs>
        
        <path 
          d="M32 4L8 14V28C8 43.5 18.5 57.5 32 62C45.5 57.5 56 43.5 56 28V14L32 4Z" 
          fill="none"
          stroke="#0A1628"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        
        <path 
          d="M22 44C22 44 26 36 32 30C38 24 42 22 42 22"
          stroke={`url(#arrowGradH-${size})`}
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        
        <path 
          d="M36 16L42 22L48 16"
          stroke={`url(#arrowGradH-${size})`}
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
      
      <span className={`font-bold ${textSize} font-['Inter','Manrope',sans-serif] tracking-tight`} style={{ color: textColor }}>
        FLOWGUARD
        <span className={`${dealsSize} font-medium ml-0.5`} style={{ color: '#FF9500' }}>.deals</span>
      </span>
    </div>
  );
};

// Icon only version - for favicon, app icon, etc.
export const FlowguardIcon = ({ size = 32, className = "" }) => {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 64 64" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id="iconArrowGrad" x1="20" y1="50" x2="44" y2="14" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FF7A00" />
          <stop offset="50%" stopColor="#FF9500" />
          <stop offset="100%" stopColor="#FFB347" />
        </linearGradient>
      </defs>
      
      {/* Shield */}
      <path 
        d="M32 4L8 14V28C8 43.5 18.5 57.5 32 62C45.5 57.5 56 43.5 56 28V14L32 4Z" 
        fill="none"
        stroke="#0A1628"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      {/* Arrow */}
      <path 
        d="M22 44C22 44 26 36 32 30C38 24 42 22 42 22"
        stroke="url(#iconArrowGrad)"
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      
      {/* Arrow head */}
      <path 
        d="M36 16L42 22L48 16"
        stroke="url(#iconArrowGrad)"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
};

export default FlowguardLogo;
